import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ImageCropperComponent,
  ImageCroppedEvent,
  LoadedImage,
} from 'ngx-image-cropper';

/** Right-angle rotations we support in this "limited features" editor. */
export type PhotoRotation = 0 | 90 | 180 | 270;

/** Result emitted on save. `dataUrl` is the downscaled+rotated source (no crop
 *  baked in). `crop` is normalized 0..1 relative to the rotated source. Backend
 *  applies crop + rotation on display so the original is preserved. */
export interface IPhotoEditResult {
  dataUrl: string;
  crop: { left: number; top: number; right: number; bottom: number };
  rotation: PhotoRotation;
}

/** Optional initial state — pass when re-editing an existing photo so the
 *  editor opens on the user's previous crop/rotation instead of default. */
export interface IPhotoEditInitial {
  crop?: { left: number; top: number; right: number; bottom: number };
  rotation?: PhotoRotation;
}

/**
 * R15/A — Shared photo editor modal used everywhere a host uploads or re-crops
 * an image. Locks aspect ratio to 1:1 by default (configurable). Rotate ±90°
 * only — no zoom UI, no filters. Uses ngx-image-cropper (MIT, Angular-native)
 * as the crop engine and the existing downscalePhoto pipeline is unchanged
 * for source bytes.
 *
 * Ported from @curbnturf/image-processing (unchanged — it was already
 * cnt-landing-only, using cnt-landing's own Tailwind brand tokens). That lib's
 * other components (image-upload, photo-display-modal, etc.) pull in legacy
 * @curbnturf/network and @curbnturf/site; importing this component via that
 * barrel dragged those in too, so it lives here instead.
 */
@Component({
  selector: 'cnt-photo-editor',
  standalone: true,
  imports: [CommonModule, ImageCropperComponent],
  templateUrl: './photo-editor.component.html',
  styleUrl: './photo-editor.component.scss',
})
export class PhotoEditorComponent {
  private platformId = inject<object>(PLATFORM_ID);

  /** Provide EXACTLY ONE of these three inputs. */
  @Input() sourceBlob: Blob | null = null;
  @Input() sourceFile: File | null = null;
  @Input() sourceDataUrl: string | null = null;

  /** Crop lock — 1:1 by default. Set to e.g. 16/9 for a hero image editor. */
  @Input() aspectRatio = 1;

  /** Cropper output size cap (longest edge). Matches downscalePhoto default. */
  @Input() maxOutputDimension = 1200;

  /** Modal title shown in the header. */
  @Input() title = 'Edit photo';

  /** Optional pre-existing edit state — re-crop flow. */
  @Input() initial: IPhotoEditInitial | null = null;

  /** Photos still waiting after this one when the caller is working through a
   *  multi-file pick. Above 0 the editor offers "Add all as-is" and "Stop adding",
   *  so a host with ten photos isn't forced through ten crops. */
  @Input() queueRemaining = 0;

  @Output() saved = new EventEmitter<IPhotoEditResult>();
  @Output() cancelled = new EventEmitter<void>();
  /** Add this photo and every queued one without cropping (queue only). */
  @Output() useRestAsIs = new EventEmitter<void>();
  /** Drop this photo and the rest of the queue (queue only). */
  @Output() stopQueue = new EventEmitter<void>();

  rotation: PhotoRotation = 0;
  /** True once the cropper has decoded the image; Save waits for it. */
  loaded = false;
  /** True when the cropper couldn't decode the file (corrupt, unsupported). */
  failed = false;
  private lastCropped: ImageCroppedEvent | null = null;
  private loadedImage: LoadedImage | null = null;

  ngOnInit(): void {
    if (this.initial?.rotation !== undefined) this.rotation = this.initial.rotation;
  }

  onCropped(evt: ImageCroppedEvent): void {
    this.lastCropped = evt;
  }

  onImageLoaded(img: LoadedImage): void {
    this.loadedImage = img;
    this.loaded = true;
  }

  onLoadFailed(): void {
    this.failed = true;
  }

  rotateLeft(): void {
    this.rotation = ((this.rotation + 270) % 360) as PhotoRotation;
  }

  rotateRight(): void {
    this.rotation = ((this.rotation + 90) % 360) as PhotoRotation;
  }

  cancel(): void {
    this.cancelled.emit();
  }

  async save(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.loadedImage) return;
    const dataUrl = await this.renderRotatedSource(this.loadedImage);
    // The crop event can trail the load event by a frame; saving in that window
    // used to drop the photo. Keep the whole image instead.
    const crop = this.lastCropped ? this.normalizeCrop(this.lastCropped) : { left: 0, top: 0, right: 1, bottom: 1 };
    this.saved.emit({ dataUrl, crop, rotation: this.rotation });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.cancel();
  }

  /** Render the source (rotated, downscaled, uncropped) to a JPEG data URL.
   *  Cropping happens on the display side — backend serves the crop rect. */
  private async renderRotatedSource(img: LoadedImage): Promise<string> {
    const source = img.transformed?.image ?? img.original.image;
    const srcW = source.width;
    const srcH = source.height;

    // Compute rotated canvas dimensions.
    const rotated = this.rotation % 180 !== 0;
    const rotatedW = rotated ? srcH : srcW;
    const rotatedH = rotated ? srcW : srcH;

    // Downscale to fit within maxOutputDimension.
    const scale = Math.min(1, this.maxOutputDimension / Math.max(rotatedW, rotatedH));
    const outW = Math.round(rotatedW * scale);
    const outH = Math.round(rotatedH * scale);

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');

    ctx.save();
    ctx.translate(outW / 2, outH / 2);
    ctx.rotate((this.rotation * Math.PI) / 180);
    ctx.drawImage(source, -srcW * scale / 2, -srcH * scale / 2, srcW * scale, srcH * scale);
    ctx.restore();

    return canvas.toDataURL('image/jpeg', 0.85);
  }

  /** Convert ngx-image-cropper's pixel positions to normalized 0..1. */
  private normalizeCrop(evt: ImageCroppedEvent): {
    left: number; top: number; right: number; bottom: number;
  } {
    const src = evt.imagePosition;
    if (!src) return { left: 0, top: 0, right: 1, bottom: 1 };
    const imgW = this.loadedImage?.transformed?.size?.width
      ?? this.loadedImage?.original.size.width
      ?? 1;
    const imgH = this.loadedImage?.transformed?.size?.height
      ?? this.loadedImage?.original.size.height
      ?? 1;
    return {
      left: src.x1 / imgW,
      top: src.y1 / imgH,
      right: src.x2 / imgW,
      bottom: src.y2 / imgH,
    };
  }
}
